import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  Taxonomy,
  TopologyEdge,
  TopologyNode,
  TopologySeed,
  TopologyView,
} from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { formatBytes } from '../attributes'
import { useAsync } from '../hooks/useAsync'
import { applyFilters, protocolsIn, serviceLabel, servicesOf, type Service } from '../topologyFilter'
import { neighbourhood } from '../topologyLayout'
import Mappa, { type ArcoMappa, type GruppoNodo, type NodoMappa, type VoceLegenda } from '../components/Mappa'
import { leggiLayout, scriviLayout, type LayoutMappa, type Posizione } from '../components/mappaLayout'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * La mappa delle relazioni fra CI, e come si esplora.
 *
 * ## Cosa non funzionava
 *
 * Un cerchio di nodi, tutti insieme, e un clic che evidenziava. Con una decina
 * di apparati bastava; oltre, la mappa mostra **tutto** e quindi non mostra
 * niente: cinquanta nodi su una circonferenza sono una matassa di corde tese
 * attraverso il centro, e l'unica cosa che si poteva fare era guardarla.
 *
 * ## Le quattro cose che la rendono esplorabile
 *
 * 1. **Il clic mette a fuoco.** Scegliere un apparato riduce la mappa al suo
 *    intorno — un salto, o due. È la differenza fra «guardare la rete» e
 *    «guardare questo armadio», e la seconda è la domanda che si fa davvero.
 * 2. **Viste separate per famiglia di relazione.** Adiacenze di rete,
 *    virtualizzazione e dipendenze di traffico sono tre mappe diverse disegnate
 *    una sopra l'altra: una VM che gira su un host e uno switch collegato a un
 *    altro switch non si leggono insieme.
 * 3. **Zoom e spostamento.** Una mappa che non si può ingrandire è un'immagine.
 * 4. **Due disposizioni.** L'anello per pochi nodi, la simulazione a forza per
 *    quando la struttura conta più dell'ordine.
 *
 * Tutto resta senza dipendenze: SVG, `viewBox` e una simulazione di sessanta
 * righe (`topologyLayout.ts`). Una libreria di grafi porterebbe centinaia di
 * kilobyte per un grafo che qui ha decine di nodi.
 */

// --- Viste ---

type Family = {
  key: string
  label: () => string
  hint: () => string
  types: string[]
}

/**
 * Le famiglie di relazione, che sono mappe diverse.
 *
 * Non sono tutti i tipi possibili: quelli che non compaiono qui finiscono in
 * «Altre relazioni», che esiste proprio perché un tipo nuovo aggiunto da un
 * connettore **si veda** invece di sparire da una mappa che dichiara di mostrare
 * tutto. Il contrario — un elenco chiuso — nasconderebbe in silenzio ciò che
 * abbiamo appena cominciato a raccogliere.
 */
const FAMILIES: Family[] = [
  {
    key: 'network',
    label: () => t('Rete'),
    hint: () => t('Adiacenze fisiche annunciate da CDP e LLDP'),
    types: ['connected_to', 'routes_to'],
  },
  {
    key: 'virtualization',
    label: () => t('Virtualizzazione'),
    hint: () => t('Dove gira una VM, su quale storage vive, a quale cluster appartiene'),
    types: ['runs_on', 'stored_on', 'member_of'],
  },
  {
    key: 'dependencies',
    label: () => t('Dipendenze'),
    hint: () => t('Comunicazioni osservate sul filo o via NetFlow'),
    types: ['depends_on', 'talks_to'],
  },
]

/** I tipi di relazione di ciascuna famiglia, per chiederli al server. */
export const FAMILY_TYPES: Record<string, string[]> = Object.fromEntries(
  FAMILIES.map((f) => [f.key, f.types]),
)

/** Come si legge un tipo di relazione. Le stesse parole del catalogo. */
const RELATION_LABELS: Record<string, () => string> = {
  connected_to: () => t('collegato a'),
  runs_on: () => t('gira su'),
  stored_on: () => t('archiviato su'),
  member_of: () => t('membro di'),
  depends_on: () => t('dipende da'),
  talks_to: () => t('parla con'),
  routes_to: () => t('instrada verso'),
  ha_peer_of: () => t('in alta affidabilità con'),
}

function relationLabel(relType: string): string {
  return RELATION_LABELS[relType]?.() ?? relType.replace(/_/g, ' ')
}

function familyOf(relType: string): string {
  return FAMILIES.find((f) => f.types.includes(relType))?.key ?? 'other'
}

/**
 * Colore del nodo per **categoria** della classe.
 *
 * Sulla categoria e non sulla classe: le classi sono una quarantina, e
 * quaranta colori non sono una legenda — sono una tavolozza in cui nessuno
 * distingue niente. Le categorie sono sei, e sono i raggruppamenti con cui si
 * ragiona.
 */
const CATEGORY_FILL: Record<string, string> = {
  network: 'var(--accent)',
  compute: 'var(--ok)',
  virtualization: 'var(--info)',
  cloud_compute: 'var(--info)',
  cloud_storage: 'var(--info)',
  cloud_network: 'var(--accent)',
  cloud_database: 'var(--info)',
  cloud_integration: 'var(--info)',
  endpoint: 'var(--warn)',
  unknown: 'var(--text-muted)',
}

export function nodeFill(category: string | null | undefined): string {
  return CATEGORY_FILL[category ?? 'unknown'] ?? 'var(--text-muted)'
}

/**
 * L'etichetta di una classe.
 *
 * Presa dalla **tassonomia del Control Plane** e non da una tabella qui: una
 * copia locale copre le classi che esistevano quando è stata scritta, e le
 * altre le mostra con il nome tecnico. Era già così — `switch`, `firewall` e
 * le venti classi cloud comparivano come `cloud instance`, `object storage`,
 * `security group` — cioè il filtro parlava una lingua diversa dal resto della
 * console.
 */
function classLabel(taxonomy: Taxonomy | null, key: string): string {
  for (const category of taxonomy?.categories ?? []) {
    const found = category.classes.find((entry) => entry.key === key)
    if (found) return t(found.label)
  }
  return key === 'unknown' ? t('non classificato') : key.replace(/_/g, ' ')
}

/**
 * Il criterio della vista gerarchica: il RUOLO dell'oggetto, dall'alto in
 * basso — dove entra il traffico, dove si commuta, dove gira il calcolo, dove
 * stanno i dati. È la disposizione con cui un sistemista disegna una rete a
 * mano, e rende la mappa leggibile senza cercare niente.
 */
const FASCE_TOPOLOGIA: { livello: number; nome: () => string; classi: string[] }[] = [
  { livello: 0, nome: () => t('Perimetro'), classi: ['firewall', 'router', 'load_balancer'] },
  { livello: 1, nome: () => t('Switching'), classi: ['switch', 'l3_switch', 'network_device', 'wireless_controller', 'virtual_switch', 'port_group'] },
  { livello: 2, nome: () => t('Cluster'), classi: ['virtual_cluster', 'compute_cluster'] },
  { livello: 3, nome: () => t('Host e dispositivi'), classi: [] },
  { livello: 4, nome: () => t('Macchine virtuali'), classi: ['virtual_machine', 'container', 'cloud_instance'] },
  { livello: 5, nome: () => t('Datastore'), classi: ['datastore'] },
  { livello: 6, nome: () => t('Storage'), classi: ['storage_system', 'storage_array'] },
]

export function livelloDi(node: TopologyNode): number {
  for (const chiave of [node.ci_class ?? '', node.ci_type]) {
    const fascia = FASCE_TOPOLOGIA.find((f) => f.classi.includes(chiave))
    if (fascia) return fascia.livello
  }
  return 3
}

export function nomeFascia(livello: number): string {
  return FASCE_TOPOLOGIA.find((f) => f.livello === livello)?.nome() ?? ''
}

/** L'icona del nodo: cos'è, prima del nome. */
export function gruppoDi(node: TopologyNode): GruppoNodo {
  const c = node.ci_class ?? node.ci_type
  if (['firewall'].includes(c)) return 'firewall'
  if (['wireless_ap', 'wireless_controller'].includes(c)) return 'wireless'
  if (['router', 'switch', 'l3_switch', 'network_device', 'load_balancer', 'virtual_switch', 'port_group'].includes(c)) return 'rete'
  if (['virtual_cluster', 'compute_cluster'].includes(c)) return 'cluster'
  if (['virtual_machine', 'container'].includes(c)) return 'virtuale'
  if (['datastore', 'storage_system', 'storage_array'].includes(c)) return 'storage'
  if ((node.ci_category ?? '').startsWith('cloud')) return 'cloud'
  if (c === 'unknown' || c === 'unmanaged_host') return 'sconosciuto'
  return 'server'
}

/** La distanza in salti dal nodo a fuoco: guida la vista radiale. */
function distanzeDa(focus: string, edges: TopologyEdge[]): Map<string, number> {
  const vicini = new Map<string, string[]>()
  for (const e of edges) {
    vicini.set(e.source, [...(vicini.get(e.source) ?? []), e.target])
    vicini.set(e.target, [...(vicini.get(e.target) ?? []), e.source])
  }
  const out = new Map<string, number>([[focus, 0]])
  const coda = [focus]
  while (coda.length) {
    const id = coda.shift()!
    for (const v of vicini.get(id) ?? []) {
      if (out.has(v)) continue
      out.set(v, out.get(id)! + 1)
      coda.push(v)
    }
  }
  return out
}

export default function Topology() {
  // La tassonomia, per dare alle classi il nome che hanno nel resto della console.
  const taxonomy = useAsync(() => api.taxonomy(), [])

  const [family, setFamily] = useState<string>('all')
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set())
  const [focus, setFocus] = useState<string | null>(null)
  const [depth, setDepth] = useState(1)

  // I tipi di relazione della famiglia scelta. Vanno al **server**: filtrare
  // dopo aver scaricato tutto significherebbe scaricare tutto, che è la cosa
  // che rendeva questa pagina inutilizzabile con la correlazione del traffico
  // accesa — `talks_to` nasce da ogni coppia che si è parlata.
  const relTypes = useMemo(
    () => (family === 'all' || family === 'other' ? undefined : FAMILY_TYPES[family]),
    [family],
  )

  // Anche la messa a fuoco è del server: espandere l'intorno nella console
  // richiederebbe di avere già in memoria l'intero grafo.
  const graph = useAsync(
    () => api.topology({ relTypes, focus, depth }),
    [relTypes, focus, depth],
  )
  const [mode, setMode] = useState<LayoutMappa>(leggiLayout())
  const [query, setQuery] = useState('')
  // Filtri sul traffico. Il dato c'era — porta, protocollo e volumi stanno
  // sugli attributi delle relazioni — e non c'era modo di usarlo.
  const [port, setPort] = useState('')
  const [protocols, setProtocols] = useState<Set<string>>(new Set())
  const [direction, setDirection] = useState<'any' | 'out' | 'in'>('any')
  // L'arco su cui si è cliccato. Prima gli archi avevano solo un tooltip: per
  // sapere su quale porta due macchine si parlano bisognava passarci sopra e
  // sperare che il testo ci stesse.
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null)
  // Le posizioni decise a mano vincono sulla disposizione calcolata. Vuoto
  // significa «nessuna decisione», non «tutto a zero»: un nodo mai spostato deve
  // continuare a seguire la simulazione anche quando altri sono stati fissati.
  const [manual, setManual] = useState<Record<string, Posizione>>({})

  const allNodes = graph.data?.nodes ?? []
  const allEdges = graph.data?.edges ?? []

  const nodesById = useMemo(() => new Map(allNodes.map((n) => [n.id, n])), [allNodes])

  // Solo le famiglie che hanno davvero degli archi: un filtro che non filtra
  // niente occupa spazio e fa dubitare che funzioni.
  const presentFamilies = useMemo(() => {
    const present = new Set(allEdges.map((e) => familyOf(e.rel_type)))
    const shown = FAMILIES.filter((f) => present.has(f.key))
    if (!present.has('other')) return shown
    return shown.concat({
      key: 'other',
      label: () => t('Altre relazioni'),
      hint: () => t('Tipi non ancora classificati in una famiglia'),
      types: [],
    })
  }, [allEdges])

  // Le **classi** presenti, non i `ci_type`.
  //
  // `ci_type` è quello che la sonda dichiara e ha una manciata di valori grossi
  // (`host`, `network_device`, `virtual_machine`): filtrarci sopra dava quattro
  // pulsanti che tolgono mezza mappa ciascuno, e non permetteva la domanda che
  // ci si fa davvero — «mostrami solo i firewall». La classe è la stessa
  // dimensione su cui si filtra in Inventario.
  const presentClasses = useMemo(
    () => Array.from(new Set(allNodes.map((n) => n.ci_class ?? 'unknown'))).sort(),
    [allNodes],
  )

  // I protocolli davvero presenti fra i collegamenti caricati. Offrire `tcp`
  // su una mappa che non ha traffico darebbe un pulsante che svuota la mappa.
  const presentProtocols = useMemo(() => protocolsIn(allEdges), [allEdges])

  // La categoria di ogni classe presente, presa dai nodi: serve a colorare il
  // pallino del filtro con lo stesso colore del nodo sulla mappa. Due colori
  // diversi per la stessa cosa renderebbero il filtro indecifrabile.
  const categoryOfClass = useMemo(() => {
    const map = new Map<string, string>()
    for (const node of allNodes) map.set(node.ci_class ?? 'unknown', node.ci_category ?? 'unknown')
    return map
  }, [allNodes])

  // --- Filtri, in ordine: famiglia → tipo di CI → messa a fuoco ---

  const needle = query.trim().toLowerCase()

  // La catena dei filtri sta in `topologyFilter.ts`, non qui.
  //
  // Ci stava, ed è il motivo per cui lo stesso difetto è sopravvissuto a due
  // giri di correzioni: dentro un componente non era verificabile da nessuna
  // parte, e l'unico modo di sapere se funzionava era aprire la pagina e
  // fidarsi di quello che si vedeva — che è esattamente ciò che non funziona
  // quando il difetto è «mostra troppo».
  const filtered = useMemo(
    () =>
      applyFilters({
        nodes: allNodes,
        edges: allEdges,
        relTypes: family === 'all' || family === 'other' ? null : (FAMILY_TYPES[family] ?? null),
        hiddenClasses: hiddenTypes,
        query,
        focus,
        depth,
        neighbourhood,
        port,
        protocols,
        direction,
      }),
    [allNodes, allEdges, family, hiddenTypes, query, focus, depth, port, protocols, direction],
  )
  const { nodes, edges, hiddenByFocus, matched: matches } = filtered

  // I nodi e gli archi come li disegna la tela condivisa.
  const nodiMappa = useMemo<NodoMappa[]>(
    () =>
      nodes.map((n) => ({
        id: n.id,
        label: n.name,
        sotto: n.mgmt_ip ?? classLabel(taxonomy.data, n.ci_class ?? 'unknown'),
        colore: nodeFill(n.ci_category),
        gruppo: gruppoDi(n),
        livello: livelloDi(n),
        seme: n.id === focus,
        trovato: matches.has(n.id),
        spento: n.lifecycle_state === 'retired',
      })),
    [nodes, taxonomy.data, focus, matches],
  )
  const tipiNodi = useMemo(() => tipiDeiNodi(nodes), [nodes])
  const archiMappa = useMemo<ArcoMappa[]>(
    () =>
      edges.map((e) => {
        const stile = edgeStyle(e, tipiNodi)
        return {
          id: e.id,
          src: e.source,
          dst: e.target,
          colore: stile.stroke,
          tratteggio: stile.dash,
          etichetta: edgeServicesLabel(e),
          titolo: edgeTitle(e),
        }
      }),
    [edges, tipiNodi],
  )
  const legenda = useMemo<VoceLegenda[]>(() => {
    const presenti = new Set(edges.map((e) => layerOf(e, tipiNodi)))
    return (['network', 'physical', 'storage', 'other'] as Layer[])
      .filter((l) => presenti.has(l))
      .map((l) => ({ etichetta: etichettaLayer(l), colore: STILE_LAYER[l].stroke, tratteggio: STILE_LAYER[l].dash }))
  }, [edges, tipiNodi])
  const distanza = useMemo(() => (focus ? distanzeDa(focus, edges) : undefined), [focus, edges])
  const fissati = useMemo(() => new Map(Object.entries(manual)), [manual])

  // I nodi fissati **e visibili**: il contatore che si mostra deve contare ciò
  // che si vede, altrimenti «12 nodi fissati» su una mappa che ne ha tre manda a
  // cercare i nove che mancano.
  const pinnedVisible = useMemo(
    () => new Set(nodes.filter((n) => manual[n.id]).map((n) => n.id)),
    [nodes, manual],
  )

  // Quando si sceglie un collegamento la mappa ci si porta sopra: la scheda
  // in basso non deve descrivere qualcosa che non si vede.
  const sceltoArco = edges.find((e) => e.id === selectedEdge) ?? null
  const inquadra = sceltoArco ? [sceltoArco.source, sceltoArco.target] : undefined

  function cambiaLayout(l: LayoutMappa) {
    setMode(l)
    scriviLayout(l)
  }

  const focusNode = focus ? nodesById.get(focus) : null
  const focusEdges = focus ? edges.filter((e) => e.source === focus || e.target === focus) : []

  function selectNode(id: string) {
    setFocus((current) => (current === id ? null : id))
  }

  function moveNode(id: string, point: Posizione) {
    setManual((current) => ({ ...current, [id]: point }))
  }

  /** Riporta i nodi fissati sotto la disposizione automatica. */
  function releasePositions() {
    setManual({})
  }

  /** Applica una vista salvata: filtri e posizioni insieme. */
  function applyView(saved: TopologyView) {
    const config = saved.config ?? {}
    // Si legge quello che si riconosce e si ignora il resto: una vista salvata
    // da una versione futura della console non deve smettere di aprirsi, e una
    // mappa che si apre senza un filtro è meglio di una che non si apre.
    if (typeof config.family === 'string') setFamily(config.family)
    if (Array.isArray(config.hidden_types)) setHiddenTypes(new Set(config.hidden_types))
    // Le viste salvate prima della mappa nuova dicono «ring»/«force»: si
    // traducono, e le loro posizioni a mano — misurate su un'altra tela — non
    // si riapplicano, perché finirebbero in punti che non vogliono dire niente.
    const legacy = config.layout === 'ring' || config.layout === 'force'
    if (config.layout === 'ring') setMode('radiale')
    else if (config.layout === 'force') setMode('organico')
    else if (config.layout === 'gerarchico' || config.layout === 'radiale' || config.layout === 'organico') {
      setMode(config.layout)
    }
    setFocus(typeof config.focus === 'string' ? config.focus : null)
    if (config.depth === 1 || config.depth === 2) setDepth(config.depth)
    setManual(legacy ? {} : saved.positions ?? {})
  }

  const currentConfig = {
    family,
    hidden_types: Array.from(hiddenTypes),
    layout: mode,
    focus,
    depth,
  }

  function toggleType(ciType: string) {
    setHiddenTypes((current) => {
      const next = new Set(current)
      if (next.has(ciType)) next.delete(ciType)
      else next.add(ciType)
      return next
    })
  }

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Topologia')}</h1>
          <p className="page__subtitle">
            {t('Descrive le relazioni tra CI, incluse adiacenze di rete, virtualizzazione e dipendenze rilevate.')}
          </p>
        </div>
        <button className="secondary" onClick={() => graph.reload()}>
          {t('Aggiorna')}
        </button>
      </header>

      <ErrorBanner error={graph.error} />

      {graph.loading && !graph.data ? (
        <Spinner />
      ) : !allNodes.length ? (
        <Card>
          <EmptyState
            title={t('Nessuna relazione rilevata')}
            hint={t('Le relazioni compaiono con una discovery che raccoglie CDP/LLDP, con i connettori di virtualizzazione o con NetFlow.')}
          />
        </Card>
      ) : (
        <>
          <SavedViews
            config={currentConfig}
            positions={manual}
            pinnedCount={pinnedVisible.size}
            onApply={applyView}
            onRelease={releasePositions}
          />

          <Card title={t('Vista')}>
            <div className="filters">
              <button
                className={`chip${family === 'all' ? ' chip--active' : ''}`}
                onClick={() => setFamily('all')}
                title={t('Tutte le relazioni insieme')}
              >
                {t('Tutto')}
              </button>
              {presentFamilies.map((f) => (
                <button
                  key={f.key}
                  className={`chip${family === f.key ? ' chip--active' : ''}`}
                  onClick={() => setFamily(f.key)}
                  title={f.hint()}
                >
                  {f.label()}
                </button>
              ))}

              <span className="filters__separator" />

              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Cerca nome o indirizzo…')}
                aria-label={t('Cerca un apparato nella mappa')}
                style={{ width: 220 }}
              />
            </div>

            {needle && (
              <p className="field__hint" style={{ marginTop: 0 }}>
                {matches.size > 0 ? (
                  matches.size === 1 ? (
                    t('La mappa mostra 1 oggetto che corrisponde a «{q}» e ciò a cui è collegato. Svuota la ricerca per rivedere tutto.', {
                      q: query.trim(),
                    })
                  ) : (
                    t('La mappa mostra {n} oggetti che corrispondono a «{q}» e ciò a cui sono collegati. Svuota la ricerca per rivedere tutto.', {
                      n: matches.size,
                      q: query.trim(),
                    })
                  )
                ) : graph.data?.truncated ? (
                  t('Nessuna corrispondenza fra i collegamenti caricati, che sono solo una parte.')
                ) : (
                  t('Nessuna corrispondenza fra i collegamenti caricati.')
                )}
              </p>
            )}

            {/* Il ripiego sulla ricerca globale compare **quando serve**: una
                seconda casella sempre visibile faceva due domande simili in due
                posti, e chi cercava un indirizzo la trovava vuota nell'una e
                inutile nell'altra. */}
            {needle && (matches.size === 0 || graph.data?.truncated) && (
              <ElsewhereInInventory
                query={query.trim()}
                onFocus={(id) => {
                  setFocus(id)
                  setQuery('')
                }}
              />
            )}

            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('I tipi di CI si nascondono e si rimettono cliccandoli. Un clic su un nodo mette a fuoco il suo intorno; un altro clic lo rilascia.')}
            </p>

            {/* Il traffico: porta, protocollo, direzione.
                Compare solo se qualche collegamento porta davvero dei servizi —
                su una mappa di sole adiacenze CDP una riga di filtri sul
                traffico non filtrerebbe niente, e un filtro che non filtra fa
                dubitare di tutti gli altri. */}
            {presentProtocols.length > 0 && (
              <div className="filters">
                <input
                  type="search"
                  value={port}
                  onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder={t('Porta…')}
                  aria-label={t('Filtra per porta')}
                  inputMode="numeric"
                  style={{ width: 110 }}
                />
                {presentProtocols.map((proto) => {
                  const on = protocols.has(proto)
                  return (
                    <button
                      key={proto}
                      className={`chip${on ? ' chip--active' : ''}`}
                      onClick={() =>
                        setProtocols((current) => {
                          const next = new Set(current)
                          if (next.has(proto)) next.delete(proto)
                          else next.add(proto)
                          return next
                        })
                      }
                    >
                      {proto.toUpperCase()}
                    </button>
                  )
                })}

                <span className="filters__separator" />

                {/* La direzione ha senso solo rispetto a un nodo: senza un
                    punto di riferimento «entrante» e «uscente» non significano
                    niente, perché ogni collegamento è entrante per uno dei due
                    capi. */}
                {(['any', 'out', 'in'] as const).map((value) => (
                  <button
                    key={value}
                    className={`chip${direction === value ? ' chip--active' : ''}${
                      !focus && value !== 'any' ? ' chip--muted' : ''
                    }`}
                    onClick={() => setDirection(value)}
                    disabled={!focus && value !== 'any'}
                    title={
                      !focus && value !== 'any'
                        ? t('Serve prima mettere a fuoco un oggetto: la direzione è rispetto a lui')
                        : undefined
                    }
                  >
                    {value === 'any' ? t('Entrambe') : value === 'out' ? t('In uscita') : t('In entrata')}
                  </button>
                ))}
              </div>
            )}

            <div className="filters">
              {presentClasses.map((ciType) => {
                const hidden = hiddenTypes.has(ciType)
                return (
                  <button
                    key={ciType}
                    className={`chip${hidden ? ' chip--muted' : ''}`}
                    onClick={() => toggleType(ciType)}
                    title={hidden ? t('Nascosto: clicca per rimetterlo') : t('Clicca per nasconderlo')}
                  >
                    <span
                      aria-hidden
                      style={{
                        display: 'inline-block',
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        marginRight: 6,
                        background: nodeFill(categoryOfClass.get(ciType)),
                      }}
                    />
                    {classLabel(taxonomy.data, ciType)}
                  </button>
                )
              })}
            </div>
          </Card>

          {graph.data?.truncated && (
            <p className="banner banner--warn">
              {/* Built here from the numbers, not from the server's Italian
                  «scope» text: it translates and keeps the counts. */}
              {(graph.data.hidden_traffic ?? 0) > 0
                ? t('La mappa visualizza un sottoinsieme delle relazioni rilevate: i {n} collegamenti più osservati su {totale}, {nascosti} collegamenti di traffico nascosti. Applica i filtri per tipologia di relazione, seleziona «Dipendenze» o un nodo per analizzarne la topologia locale.', {
                    n: graph.data.edges.length, totale: graph.data.total_edges, nascosti: graph.data.hidden_traffic,
                  })
                : t('La mappa visualizza un sottoinsieme delle relazioni rilevate: i {n} collegamenti più osservati su {totale}. Applica i filtri per tipologia di relazione, seleziona «Dipendenze» o un nodo per analizzarne la topologia locale.', {
                    n: graph.data.edges.length, totale: graph.data.total_edges,
                  })}
            </p>
          )}

          {/* Il traffico è nascosto di default: è la nuvola che rendeva la mappa
              inservibile. Lo si dice, con il numero, e lo si accende in un
              click — ma dopo aver scelto un punto di partenza, dove «chi parla
              con chi» ha una risposta leggibile. */}
          {family === 'all' && !focus && (graph.data?.hidden_traffic ?? 0) > 0 && (
            <p className="banner">
              {(() => {
                // Una frase sola con il pulsante in mezzo: si traduce intera e si
                // spezza sul segnaposto, così ogni lingua tiene il proprio ordine.
                const [prima, dopo] = t('La mappa rappresenta la topologia di rete; {n} collegamenti di traffico nascosti sono visualizzabili selezionando un asset o la vista {dipendenze}.', {
                  n: graph.data!.hidden_traffic,
                }).split('{dipendenze}')
                return (
                  <>
                    {prima}
                    <button type="button" className="linklike"
                            onClick={() => setFamily('dependencies')}>
                      {t('Dipendenze')}
                    </button>
                    {dopo}
                  </>
                )
              })()}
            </p>
          )}

          <Card
            title={t('{n} nodi, {m} collegamenti', { n: nodes.length, m: edges.length })}
            actions={
              <div className="filters" style={{ margin: 0 }}>
                {focusNode && (
                  <>
                    <button className="chip chip--active" onClick={() => setFocus(null)}>
                      {focusNode.name} ✕
                    </button>
                    <button
                      className={`chip${depth === 1 ? ' chip--active' : ''}`}
                      onClick={() => setDepth(1)}
                      title={t('Solo ciò che è collegato direttamente')}
                    >
                      {t('1 salto')}
                    </button>
                    <button
                      className={`chip${depth === 2 ? ' chip--active' : ''}`}
                      onClick={() => setDepth(2)}
                      title={t('Anche ciò che sta dietro i vicini')}
                    >
                      {t('2 salti')}
                    </button>
                  </>
                )}
              </div>
            }
          >
            {nodes.length === 0 ? (
              <EmptyState
                title={t('Niente da mostrare con questi filtri')}
                hint={t('Nessuna relazione della famiglia scelta collega due tipi di CI visibili.')}
              />
            ) : (
              <>
                <Mappa
                  nodi={nodiMappa}
                  archi={archiMappa}
                  layout={mode}
                  onLayout={cambiaLayout}
                  distanza={distanza}
                  nomiFasce={nomeFascia}
                  fissati={fissati}
                  onFissa={moveNode}
                  selezionato={focus}
                  arcoScelto={selectedEdge}
                  onClickNodo={(id) => selectNode(id)}
                  onClickArco={(id) => setSelectedEdge((c) => (c === id ? null : id))}
                  onClickSfondo={() => setSelectedEdge(null)}
                  inquadra={inquadra}
                  legenda={legenda}
                  etichetta={t('Mappa delle relazioni fra configuration item')}
                />
                <EdgeDetail
                  edge={sceltoArco}
                  nodesById={nodesById}
                  onClose={() => setSelectedEdge(null)}
                  onFocusNode={(id) => {
                    setFocus(id)
                    setSelectedEdge(null)
                  }}
                />
                <p className="field__hint">
                  {t('Rotella o pizzico per ingrandire, trascinamento della tela per spostare, trascinamento di un nodo per fissarlo (contorno tratteggiato). Passando su un nodo si accendono i suoi collegamenti; un clic lo mette a fuoco. Tasti: + − e 0 per adattare.')}
                  {pinnedVisible.size > 0 &&
                    ' ' + t('{n} nodi fissati.', { n: pinnedVisible.size })}
                  {hiddenByFocus > 0 &&
                    ' ' + t('{n} nodi fuori dall’intorno non sono mostrati.', { n: hiddenByFocus })}
                  {query.trim() &&
                    ' ' +
                      (matches.size === 0
                        ? t('0 nodi corrispondono a «{q}» — potrebbero essere nascosti da un filtro.', {
                            q: query.trim(),
                          })
                        : t('{n} nodi corrispondono a «{q}».', { n: matches.size, q: query.trim() }))}
                </p>
              </>
            )}
          </Card>

          {focusNode && (
            <Card title={t('Collegamenti di {nome}', { nome: focusNode.name })}>
              <p style={{ marginTop: 0 }}>
                <Link to={`/cis/${focusNode.id}`}>{t('Apri la scheda del CI →')}</Link>
              </p>
              <EdgeTable
                edges={focusEdges}
                nodesById={nodesById}
                selected={focusNode.id}
                onSelect={selectNode}
                selectedEdge={selectedEdge}
                onSelectEdge={setSelectedEdge}
              />
            </Card>
          )}
        </>
      )}
    </>
  )
}

/**
 * La tela: SVG con `viewBox` mobile.
 *
 * Zoom e spostamento sono fatti muovendo il `viewBox` invece di applicare una
 * trasformazione ai nodi: così i tratti restano dello stesso spessore e le
 * etichette della stessa dimensione a qualunque ingrandimento — con una
 * `transform` diventerebbero illeggibili proprio quando si ingrandisce per
 * leggerle.
 */
/**
 * Il ripiego quando la ricerca non trova niente **fra i collegamenti caricati**.
 *
 * Il grafo che la pagina ha in memoria è una parte: sopra il tetto il Control
 * Plane manda i collegamenti più osservati e dichiara il resto. Cercare un
 * indirizzo lì dentro e non trovarlo non significa che l'apparato non ci sia —
 * significa che non è fra quelli scaricati, che è una notizia diversa e porta a
 * un'azione diversa.
 *
 * Quindi si chiede al Control Plane, e il risultato **mette a fuoco**: da lì la
 * mappa si ricarica intorno a quel nodo, che è la sola forma in cui una
 * topologia grande si guarda.
 */
function ElsewhereInInventory({
  query,
  onFocus,
}: {
  query: string
  onFocus: (id: string) => void
}) {
  const [results, setResults] = useState<TopologySeed[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function search() {
    setBusy(true)
    setError(null)
    try {
      setResults(await api.topologySearch(query))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <button className="secondary" onClick={search} disabled={busy}>
        {busy ? t('Cerco…') : t('Cerca «{q}» in tutta la topologia', { q: query })}
      </button>

      <ErrorBanner error={error} />

      {results !== null &&
        (results.length === 0 ? (
          <p className="field__hint">
            {t('Nessun oggetto con questo nome o indirizzo partecipa a una relazione. I CI senza collegamenti non compaiono in mappa: cercali in Inventario.')}
          </p>
        ) : (
          <div className="filters" style={{ marginTop: 8 }}>
            {results.map((seed) => (
              <button key={seed.id} className="chip" onClick={() => onFocus(seed.id)}>
                {seed.name}
                {seed.mgmt_ip ? ` · ${seed.mgmt_ip}` : ''}
              </button>
            ))}
          </div>
        ))}
    </div>
  )
}


// --- Come si legge un arco ---

/**
 * Il protocollo di una relazione, che non è la fonte da cui la sappiamo.
 *
 * `via` dice **come** l'abbiamo saputo: `cdp`, `lldp`, `netflow`, `vmware_api`.
 * Per CDP e LLDP le due cose coincidono — sono protocolli di adiacenza — ma per
 * una relazione letta da un'API non esiste nessun protocollo di rete.
 */
/**
 * Come si riassume in una riga cosa passa su un collegamento.
 *
 * Cercava `attributes.protocol` e `attributes.port`, campi **piatti**. Le
 * relazioni di traffico portano invece `services`, una lista — fra due macchine
 * passano più cose. Per ogni collegamento di dipendenza non trovava niente:
 * nessun errore, nessuna etichetta, e la conclusione che il protocollo non
 * fosse stato raccolto. Ora legge le due forme con la stessa funzione del
 * pannello di dettaglio, così l'etichetta e la scheda non possono divergere.
 */
function edgeProtocol(edge: TopologyEdge): string | null {
  const services = servicesOf(edge)
  if (services.length) {
    const first = serviceLabel(services[0])
    // «443/tcp +2» invece di elencarne dieci: la mappa deve restare leggibile,
    // e il resto è a un clic.
    return services.length > 1 ? `${first} +${services.length - 1}` : first
  }
  const via = String((edge.attributes ?? {}).via ?? '').toLowerCase()
  if (via === 'cdp' || via === 'lldp') return via.toUpperCase()
  return null
}

/**
 * I tre layer di una relazione, che la mappa colora in modo diverso: fra due
 * oggetti possono esserci più relazioni — un host **collegato a** uno switch,
 * **membro di** un cluster, e con i dati **archiviati su** un datastore — e
 * se hanno lo stesso colore il fascio si legge come un filo solo.
 *
 * - **storage**: dove stanno i dati — `stored_on`, e ogni collegamento che
 *   tocca un datastore o un array (un host che monta un datastore);
 * - **network**: la rete — adiacenze, instradamento, traffico osservato;
 * - **fisico**: dove un oggetto sta o gira — VM sull'host, host nel cluster,
 *   la parte nel suo telaio.
 */
type Layer = 'storage' | 'network' | 'physical' | 'other'

const TIPI_STORAGE = new Set(['datastore', 'storage_system', 'storage_array', 'storage'])

function layerOf(edge: TopologyEdge, tipi?: Map<string, string[]>): Layer {
  const tocca = (id: string) => (tipi?.get(id) ?? []).some((x) => TIPI_STORAGE.has(x))
  switch (edge.rel_type) {
    case 'stored_on':
      return 'storage'
    case 'connected_to':
      return tocca(edge.source) || tocca(edge.target) ? 'storage' : 'network'
    case 'routes_to':
    case 'talks_to':
    case 'depends_on':
      return 'network'
    case 'runs_on':
    case 'member_of':
    case 'part_of':
      return 'physical'
    default:
      return 'other'
  }
}

/**
 * Tratto e colore per layer.
 *
 * Il colore da solo non basta: chi non lo distingue vedrebbe un'unica matassa.
 * Il tratteggio porta la stessa informazione e sopravvive alla stampa in bianco
 * e nero — è la stessa ragione per cui gli stati sono un pallino *più*
 * un'etichetta e non solo un colore. La terna di colori è quella già validata
 * per la visione dei colori su tema chiaro e scuro (vedi la sezione Wireless).
 */
const STILE_LAYER: Record<Layer, { stroke: string; dash?: string }> = {
  storage: { stroke: '#d97706', dash: '6 4' },
  network: { stroke: '#3b82f6' },
  physical: { stroke: '#16a34a', dash: '2 3' },
  other: { stroke: 'var(--text-muted)', dash: '1 4' },
}

function edgeStyle(edge: TopologyEdge, tipi?: Map<string, string[]>): { stroke: string; dash?: string } {
  return STILE_LAYER[layerOf(edge, tipi)]
}

/** L'etichetta di un layer, tradotta con letterali (la guardia li riconosce). */
function etichettaLayer(layer: Layer): string {
  switch (layer) {
    case 'storage':
      return t('Layer storage')
    case 'network':
      return t('Layer network')
    case 'physical':
      return t('Layer fisico')
    default:
      return t('Altre relazioni')
  }
}

/** Tipo e classe di ogni nodo, per decidere il layer di un arco. */
function tipiDeiNodi(nodes: Iterable<TopologyNode>): Map<string, string[]> {
  const fuori = new Map<string, string[]>()
  for (const n of nodes) fuori.set(n.id, [n.ci_type, n.ci_class ?? ''])
  return fuori
}

/** Cosa scrivere sull'arco quando ce n'è più di uno fra gli stessi due nodi. */
function edgeLabel(edge: TopologyEdge): string {
  return edgeProtocol(edge) ?? relationLabel(edge.rel_type)
}

/**
 * L'etichetta estesa di un arco: le porte, non «la prima +N».
 *
 * Serve nell'intorno di un nodo: lì la domanda è «su cosa si parlano queste
 * macchine», e «443/tcp +2» risponde a metà — le altre due porte sono proprio
 * quelle che si stavano cercando. Tre e non tutte: oltre, l'etichetta copre la
 * mappa, e il resto sta nella scheda del collegamento a un clic.
 *
 * L'ordine è per traffico: se le porte visibili sono tre su cinque, siano le
 * tre su cui passa qualcosa — non le prime arrivate dalla raccolta.
 */
function edgeServicesLabel(edge: TopologyEdge, max = 3): string {
  const services = [...servicesOf(edge)].sort((a, b) => (b.bytes ?? 0) - (a.bytes ?? 0))
  if (!services.length) return edgeLabel(edge)
  const shown = services.slice(0, max).map(serviceLabel).join(' · ')
  return services.length > max ? `${shown} +${services.length - max}` : shown
}

function edgeTitle(edge: TopologyEdge): string {
  const protocol = edgeProtocol(edge)
  const via = (edge.attributes ?? {}).via
  return [
    relationLabel(edge.rel_type),
    protocol ? t('protocollo {protocollo}', { protocollo: protocol }) : null,
    via ? t('rilevato tramite {fonte}', { fonte: String(via) }) : null,
  ]
    .filter(Boolean)
    .join(' — ')
}

/**
 * Cosa passa su un collegamento, quando ci si clicca sopra.
 *
 * Prima gli archi avevano un tooltip e basta, e il tooltip non mostrava il
 * traffico: `edgeProtocol` cercava `attributes.protocol` e `attributes.port`,
 * campi **piatti**, mentre le relazioni di traffico portano
 * `services: [{port, protocol, name, bytes}]` — una lista, perché fra due
 * macchine passano più cose. Il dato c'era, la console lo cercava dove non
 * era: nessun errore, nessuna etichetta, e la conclusione che il protocollo
 * non fosse stato raccolto.
 *
 * La direzione è nell'ordine dei due capi, e va scritta: un arco fra A e B
 * senza il verso non dice chi ha aperto la connessione, che è metà
 * dell'informazione quando si guarda una dipendenza.
 */
/** The columns of the services on a link (a function: `t()` at render time). */
const COLONNE_SERVIZI = (): Column<Service>[] => [
  { key: 'port', header: t('Porta'), mono: true },
  { key: 'protocol', header: t('Protocollo'), value: (service) => (service.protocol ? service.protocol.toUpperCase() : '') },
  {
    key: 'name',
    header: t('Servizio'),
    render: (service) => service.name || <span className="text-muted">{t('non riconosciuto')}</span>,
  },
  {
    key: 'bytes',
    header: t('Volume'),
    type: 'number',
    render: (service) => (service.bytes !== null ? formatBytes(service.bytes) : '—'),
  },
]

function EdgeDetail({
  edge,
  nodesById,
  onClose,
  onFocusNode,
}: {
  edge: TopologyEdge | null
  nodesById: Map<string, TopologyNode>
  onClose: () => void
  onFocusNode: (id: string) => void
}) {
  if (!edge) return null

  const attrs = edge.attributes ?? {}
  const source = nodesById.get(edge.source)
  const target = nodesById.get(edge.target)
  const services = servicesOf(edge)
  const via = attrs.via ? String(attrs.via) : null
  const windowHours = typeof attrs.window_hours === 'number' ? attrs.window_hours : null

  return (
    <Card
      title={t('Collegamento selezionato')}
      actions={
        <button className="secondary" onClick={onClose}>
          {t('Chiudi')}
        </button>
      }
    >
      <p>
        <strong>{source?.name ?? '—'}</strong>
        {' → '}
        <strong>{target?.name ?? '—'}</strong>{' '}
        <span className="badge badge--info">{relationLabel(edge.rel_type)}</span>
      </p>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('La freccia è il verso: {sorgente} apre la connessione verso {destinazione}. Le porte qui sotto sono quelle di destinazione.', {
          sorgente: source?.name ?? t('il primo'),
          destinazione: target?.name ?? t('il secondo'),
        })}
      </p>

      {/* Da un collegamento si va **avanti**: si centra uno dei due capi e la
          mappa si ricostruisce intorno a lui. È il modo in cui si segue una
          dipendenza — «e questo, con chi altro parla?» — e senza questi due
          pulsanti la scheda è un vicolo cieco: dice cosa passa e non porta da
          nessuna parte. */}
      <div className="filters">
        {source && (
          <button className="chip" onClick={() => onFocusNode(source.id)}>
            {t('Centra su {nome}', { nome: source.name })}
          </button>
        )}
        {target && (
          <button className="chip" onClick={() => onFocusNode(target.id)}>
            {t('Centra su {nome}', { nome: target.name })}
          </button>
        )}
        {source && (
          <Link className="chip" to={`/cis/${source.id}`}>
            {t('Scheda di {nome}', { nome: source.name })}
          </Link>
        )}
        {target && (
          <Link className="chip" to={`/cis/${target.id}`}>
            {t('Scheda di {nome}', { nome: target.name })}
          </Link>
        )}
      </div>

      {services.length === 0 ? (
        <p className="text-muted">
          {t('Nessuna porta o protocollo su questo collegamento. Le adiacenze di rete (CDP, LLDP) e le relazioni di virtualizzazione non ne hanno: non è un dato mancante, è un tipo di collegamento che non ne prevede.')}
        </p>
      ) : (
        <DataTable
          rows={services}
          columns={COLONNE_SERVIZI()}
          rowKey={(service, index) => `${service.protocol}-${service.port}-${index}`}
          conteggio={{ nome: t('porte') }}
        />
      )}

      <dl className="kv">
        {typeof attrs.bytes === 'number' && (
          <>
            <dt>{t('Volume totale')}</dt>
            <dd>
              {formatBytes(attrs.bytes)}
              {/* Senza la finestra, «4 GB» non significa niente: potrebbero
                  essere di un'ora o di un mese. */}
              {windowHours ? ' ' + t('nelle ultime {n} ore', { n: windowHours }) : ''}
            </dd>
          </>
        )}
        {typeof attrs.flows === 'number' && (
          <>
            <dt>{t('Conversazioni')}</dt>
            <dd>{attrs.flows.toLocaleString(localeCorrente())}</dd>
          </>
        )}
        {via && (
          <>
            <dt>{t('Rilevato tramite')}</dt>
            <dd>{via}</dd>
          </>
        )}
        <>
          <dt>{t('Visto')}</dt>
          <dd>
            {edge.last_seen
              ? t('{n} volte, l’ultima {quando}', {
                  n: edge.observations ?? 1,
                  quando: formatDateTime(edge.last_seen),
                })
              : t('{n} volte', { n: edge.observations ?? 1 })}
          </dd>
        </>
      </dl>
    </Card>
  )
}

function EdgeTable({
  edges,
  nodesById,
  selected,
  onSelect,
  selectedEdge,
  onSelectEdge,
}: {
  edges: TopologyEdge[]
  nodesById: Map<string, TopologyNode>
  selected: string
  onSelect: (id: string) => void
  /** Il collegamento evidenziato **sulla mappa**: la riga corrispondente si segna. */
  selectedEdge: string | null
  onSelectEdge: (id: string | null) => void
}) {
  if (edges.length === 0) return <EmptyState title={t('Nessun collegamento')} />

  // Le colonne delle porte esistono solo per CDP e LLDP: su una relazione di
  // virtualizzazione resterebbero vuote su ogni riga, e una colonna sempre vuota
  // fa concludere che la discovery non le ha raccolte invece che quel tipo di
  // collegamento non ne ha.
  const has = (key: string) => edges.some((e) => (e.attributes ?? {})[key] != null)
  const showPorts = has('local_port') || has('remote_port')
  // I servizi: la stessa lettura della mappa e della scheda. Le colonne
  // `local_port`/`remote_port` restano perché su CDP e LLDP dicono qualcosa che
  // i servizi non dicono — su quale porta *fisica* passa l'adiacenza — ma sul
  // traffico erano vuote, ed è per questo che «non c'era modo di vedere le
  // porte».
  const showServices = edges.some((e) => servicesOf(e).length > 0)

  const peerOf = (e: TopologyEdge) => nodesById.get(e.source === selected ? e.target : e.source)
  const colonne: Column<TopologyEdge>[] = [
    {
      key: 'peer',
      header: t('Dispositivo collegato'),
      value: (e) => peerOf(e)?.name ?? '',
      render: (e) => {
        const peer = peerOf(e)
        return peer ? (
          <>
            <Link to={`/cis/${peer.id}`}>{peer.name}</Link>{' '}
            <button
              className="link"
              onClick={() => onSelect(peer.id)}
              title={t('Sposta la messa a fuoco su questo nodo')}
            >
              {t('(centra)')}
            </button>
          </>
        ) : (
          '—'
        )
      },
    },
    { key: 'rel_type', header: t('Collegamento'), value: (e) => relationLabel(e.rel_type) },
    {
      key: 'direzione',
      header: t('Direzione'),
      value: (e) => (e.source === selected ? `→ ${t('uscente')}` : `← ${t('entrante')}`),
    },
    ...(showPorts
      ? [
          { key: 'local_port', header: t('Porta locale'), mono: true, value: (e: TopologyEdge) => String((e.attributes ?? {}).local_port ?? '') },
          { key: 'remote_port', header: t('Porta remota'), mono: true, value: (e: TopologyEdge) => String((e.attributes ?? {}).remote_port ?? '') },
        ]
      : []),
    ...(showServices
      ? [
          {
            key: 'services',
            header: t('Porte e protocolli'),
            mono: true,
            value: (e: TopologyEdge) => servicesOf(e).map(serviceLabel).join(', '),
            render: (e: TopologyEdge) =>
              servicesOf(e).length ? servicesOf(e).map(serviceLabel).join(', ') : <span className="text-muted">—</span>,
          },
        ]
      : []),
    { key: 'via', header: t('Rilevato tramite'), mono: true, value: (e) => String((e.attributes ?? {}).via ?? '') },
    {
      key: 'azioni',
      header: '',
      sort: false,
      filter: false,
      searchable: false,
      render: (e) => {
        const chosen = e.id === selectedEdge
        return (
          <button
            className="link"
            onClick={() => onSelectEdge(chosen ? null : e.id)}
            title={
              chosen
                ? t('Togli l’evidenza dalla mappa')
                : t('Evidenzia questo collegamento sulla mappa e portacisi sopra')
            }
          >
            {chosen ? t('nascondi') : t('mostra in mappa')}
          </button>
        )
      },
    },
  ]

  // The row and the line are the same thing seen from two sides: marking only
  // one of them would leave it to the eye to link them back, which on a bundle
  // of parallel edges does not work.
  return (
    <DataTable
      rows={edges}
      columns={colonne}
      rowKey={(e) => e.id}
      conteggio={{ nome: t('collegamenti') }}
      isSelected={(e) => e.id === selectedEdge}
    />
  )
}

/**
 * Le viste salvate: filtri **e** posizioni dei nodi, con un nome.
 *
 * ## Perché sul server e non nel browser
 *
 * Disporre trenta apparati fino a far somigliare la mappa all'armadio vero è
 * lavoro. In `localStorage` quel lavoro vive su un computer solo: si perde
 * cambiando browser, non si vede da telefono, e soprattutto non si può mostrare
 * a un collega — che è il momento in cui una mappa disposta a mano serve
 * davvero. Sul server la vista è del tenant, e chiunque abbia accesso a quel
 * cliente la riapre com'è stata lasciata.
 *
 * ## Perché salvare anche i filtri
 *
 * Una posizione senza il filtro che la giustifica è una mappa mezza vuota: le
 * coordinate degli apparati di rete, riaperte sulla vista «virtualizzazione»,
 * non hanno niente a cui applicarsi. Le due cose si salvano insieme perché è
 * insieme che si sono decise.
 */
function SavedViews({
  config,
  positions,
  pinnedCount,
  onApply,
  onRelease,
}: {
  config: Record<string, unknown>
  positions: Record<string, Posizione>
  pinnedCount: number
  onApply: (view: TopologyView) => void
  onRelease: () => void
}) {
  const views = useAsync(() => api.topologyViews(), [])
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [outcome, setOutcome] = useState<string | null>(null)

  async function save() {
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    setOutcome(null)
    try {
      // Un nome già usato aggiorna quella vista invece di fallire: «salva»
      // premuto due volte sulla stessa mappa è il gesto normale, e un errore di
      // vincolo unico lì costringerebbe a inventare nomi progressivi.
      await api.saveTopologyView({ name: trimmed, config, positions })
      setOutcome(
        t('Vista «{nome}» salvata con {n} nodi fissati.', {
          nome: trimmed,
          n: Object.keys(positions).length,
        }),
      )
      views.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove(view: TopologyView) {
    if (!window.confirm(t('Eliminare la vista «{nome}»? Non si può annullare.', { nome: view.name }))) return
    setBusy(true)
    setError(null)
    try {
      await api.deleteTopologyView(view.id)
      views.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Viste salvate')}>
      <ErrorBanner error={views.error ?? error} />

      <div className="filters">
        {(views.data ?? []).map((v) => (
          <span key={v.id} className="chip chip--pair">
            <button className="link" onClick={() => onApply(v)} title={v.description ?? ''}>
              {v.name}
            </button>
            <button
              className="link chip__remove"
              onClick={() => remove(v)}
              disabled={busy}
              aria-label={t('Elimina la vista {nome}', { nome: v.name })}
              title={t('Elimina questa vista')}
            >
              ✕
            </button>
          </span>
        ))}
        {views.data?.length === 0 && (
          <span className="text-muted" style={{ fontSize: 13 }}>
            {t('Nessuna vista salvata.')}
          </span>
        )}
      </div>

      <div className="filters">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
          }}
          placeholder={t('Nome della vista…')}
          aria-label={t('Nome della vista da salvare')}
          style={{ width: 240 }}
        />
        <button onClick={save} disabled={busy || !name.trim()}>
          {busy ? t('Salvataggio…') : t('Salva vista')}
        </button>
        {pinnedCount > 0 && (
          <button className="secondary" onClick={onRelease} disabled={busy}>
            {t('Libera {n} nodi fissati', { n: pinnedCount })}
          </button>
        )}
      </div>

      {outcome && <div className="banner banner--ok">{outcome}</div>}

      <p className="field__hint" style={{ marginBottom: 0 }}>
        {t('Salva filtri e posizioni dei nodi in una vista condivisa con tutti gli utenti del tenant autorizzati.')}
      </p>
    </Card>
  )
}

